import { useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { toast } from 'sonner'
import { Plus } from 'lucide-react'
import { AP_INVOICE_PRIORITIES, AP_INVOICE_STATUSES } from '../constants'
import { useCreateInvoice } from '../mutations'
import { useUploadInvoicePdf } from '../data/documents'
import { validateInvoicePdf } from '../pdf-upload'
import { InvoicePdfDropZone } from '../pdf-drop-zone'
import type { ApInvoiceCreateInput } from '../types'
import { formatInvoicePriority, formatInvoiceStatus } from '../status'
import { InlineFieldInput, InlineFieldTextarea } from '@/components/inline-field-group'
import { LoadingButton } from '@/components/loading-button'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { SearchSelect } from '@/components/search-select'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { getApiErrorMessage } from '@/lib/api-error'

const defaultInvoiceForm: ApInvoiceCreateInput = {
  invoice_number: '',
  vendor_raw: '',
  invoice_total: '',
  invoice_date: '',
  official_due_date: '',
  priority_due_date: '',
  status: 'needs_approval',
  priority_status: 'pay_now',
  payment_terms_raw: '',
  project_ship_to_raw: '',
  notes_lesley: '',
  notes_pc: '',
}

function validateInvoiceForm(form: ApInvoiceCreateInput): string | null {
  if (!form.invoice_number.trim()) return 'Invoice number is required'
  if (!form.vendor_raw.trim()) return 'Vendor is required'
  if (form.invoice_total !== '' && form.invoice_total != null) {
    const amount = Number(form.invoice_total)
    if (!Number.isFinite(amount) || amount < 0) return 'Invoice total must be a non-negative number'
  }
  return null
}

export function CreateInvoiceDialog() {
  const [open, setOpen] = useState(false)
  const [form, setForm] = useState<ApInvoiceCreateInput>(defaultInvoiceForm)
  const [pdf, setPdf] = useState<File | null>(null)
  const createInvoice = useCreateInvoice()
  const uploadPdf = useUploadInvoicePdf()
  const navigate = useNavigate()

  const update = (patch: Partial<ApInvoiceCreateInput>) =>
    setForm((current) => ({ ...current, ...patch }))

  const handleSubmit = () => {
    const error = validateInvoiceForm(form) ?? (pdf && validateInvoicePdf(pdf))
    if (error) {
      toast.error(error)
      return
    }
    createInvoice.mutate(
      {
        ...form,
        invoice_number: form.invoice_number.trim(),
        vendor_raw: form.vendor_raw.trim(),
        invoice_total: form.invoice_total || null,
        invoice_date: form.invoice_date || null,
        official_due_date: form.official_due_date || null,
        priority_due_date: form.priority_due_date || null,
        payment_terms_raw: form.payment_terms_raw || null,
        project_ship_to_raw: form.project_ship_to_raw || null,
        notes_lesley: form.notes_lesley || null,
        notes_pc: form.notes_pc || null,
      },
      {
        onSuccess: (invoice) => {
          const openInvoice = () => {
            setOpen(false)
            setForm(defaultInvoiceForm)
            setPdf(null)
            navigate({
              to: '/invoices/$invoiceId',
              params: { invoiceId: invoice.uuid! },
            })
          }
          if (!pdf) {
            toast.success('Invoice draft created')
            openInvoice()
            return
          }
          uploadPdf.mutate(
            { file: pdf, invoiceUuid: invoice.uuid! },
            {
              onSuccess: () => toast.success('Invoice draft created with PDF'),
              onError: (err) =>
                toast.error(
                  `Invoice created, but the PDF didn't attach (${
                    getApiErrorMessage(err, 'upload failed')
                  }). Upload it again from the invoice page.`,
                ),
              onSettled: openInvoice,
            },
          )
        },
        onError: (err) => toast.error(getApiErrorMessage(err, 'Invoice create failed')),
      },
    )
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <Plus className="mr-2 h-4 w-4" /> New invoice
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Create AP invoice</DialogTitle>
          <DialogDescription>
            Manual AP records update the authenticated invoice API only. This does not send email,
            execute payment, or delete records.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="invoice-number">Invoice #</Label>
            <InlineFieldInput
              id="invoice-number"
              value={form.invoice_number}
              onChange={(event) => update({ invoice_number: event.target.value })}
              placeholder="SO207465"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="vendor">Vendor</Label>
            <InlineFieldInput
              id="vendor"
              value={form.vendor_raw}
              onChange={(event) => update({ vendor_raw: event.target.value })}
              placeholder="Worldwide Express"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="invoice-total">Invoice total</Label>
            <InlineFieldInput
              id="invoice-total"
              inputMode="decimal"
              value={String(form.invoice_total ?? '')}
              onChange={(event) => update({ invoice_total: event.target.value })}
              placeholder="1861.17"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="invoice-status">Status</Label>
            <SearchSelect
              id="invoice-status"
              className="h-9 w-full"
              value={form.status ?? undefined}
              options={AP_INVOICE_STATUSES.map((status) => ({
                value: status,
                label: formatInvoiceStatus(status),
              }))}
              onChange={(status) => status && update({ status })}
              searchPlaceholder="Search statuses"
            />
          </div>
          <div className="space-y-2">
            <Label>Priority</Label>
            <Select
              value={form.priority_status ?? 'none'}
              onValueChange={(priorityStatus) => update({ priority_status: priorityStatus })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {AP_INVOICE_PRIORITIES.map((priority) => (
                  <SelectItem key={priority} value={priority}>
                    {formatInvoicePriority(priority)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="terms">Payment terms</Label>
            <InlineFieldInput
              id="terms"
              value={form.payment_terms_raw ?? ''}
              onChange={(event) => update({ payment_terms_raw: event.target.value })}
              placeholder="Net 30"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="invoice-date">Invoice date</Label>
            <InlineFieldInput
              id="invoice-date"
              type="date"
              value={form.invoice_date ?? ''}
              onChange={(event) => update({ invoice_date: event.target.value })}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="due-date">Official due date</Label>
            <InlineFieldInput
              id="due-date"
              type="date"
              value={form.official_due_date ?? ''}
              onChange={(event) => update({ official_due_date: event.target.value })}
            />
          </div>
          <div className="space-y-2 sm:col-span-2">
            <Label htmlFor="project-ship-to">Project / ship to</Label>
            <InlineFieldInput
              id="project-ship-to"
              value={form.project_ship_to_raw ?? ''}
              onChange={(event) => update({ project_ship_to_raw: event.target.value })}
              placeholder="Kitchen Equipment Contractors"
            />
          </div>
          <div className="space-y-2 sm:col-span-2">
            <Label htmlFor="notes">Accounting notes</Label>
            <InlineFieldTextarea
              id="notes"
              value={form.notes_lesley ?? ''}
              onChange={(event) => update({ notes_lesley: event.target.value })}
              placeholder="Payment or accounting note"
            />
          </div>
          <div className="space-y-2 sm:col-span-2">
            <Label htmlFor="new-invoice-pdf">Invoice PDF</Label>
            <InvoicePdfDropZone inputId="new-invoice-pdf" file={pdf} onFileChange={setPdf} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <LoadingButton
            loading={createInvoice.isPending || uploadPdf.isPending}
            loadingText="Creating..."
            onClick={handleSubmit}
          >
            <Plus className="mr-2 h-4 w-4" /> Create invoice
          </LoadingButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
